import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const LIGHT_TILES = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}";

// heart-ish pink marker
const pinIcon = L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;background:#F43F5E;border:3px solid #fff;border-radius:50%;box-shadow:0 0 14px rgba(244,63,94,0.9)"></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const memberIcon = L.divIcon({
  className: "",
  html: `<div style="width:16px;height:16px;background:#0ea5e9;border:2px solid #fff;border-radius:50%;box-shadow:0 0 10px rgba(14,165,233,0.8)"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

export function LeafletMap({ lat, lng, onPick, markers = [], height = "100%", interactive = true, zoom = 13, drift = false }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const extraLayerRef = useRef(null);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const startLat = lat ?? 40.7128;
    const startLng = lng ?? -74.006;
    const map = L.map(containerRef.current, {
      center: [startLat, startLng],
      zoom,
      zoomControl: interactive,
      attributionControl: true,
      dragging: interactive,
      scrollWheelZoom: interactive,
      doubleClickZoom: interactive,
    });
    L.tileLayer(LIGHT_TILES, { attribution: "Tiles © Esri", maxZoom: 19 }).addTo(map);
    mapRef.current = map;

    map.on("click", (e) => { if (onPickRef.current) onPickRef.current(e.latlng.lat, e.latlng.lng); });
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; markerRef.current = null; extraLayerRef.current = null; };
    // eslint-disable-next-line
  }, []);

  // single picked marker
  useEffect(() => {
    const map = mapRef.current;
    if (!map || lat == null || lng == null) return;
    if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
    else markerRef.current = L.marker([lat, lng], { icon: pinIcon }).addTo(map);
    map.setView([lat, lng], map.getZoom());
  }, [lat, lng]);

  // multiple markers, optionally drifting ~a block around home every 3-5s
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (extraLayerRef.current) { map.removeLayer(extraLayerRef.current); extraLayerRef.current = null; }
    if (!markers.length) return;
    const group = L.layerGroup();
    const pins = markers.map((m) => ({
      home: [m.lat, m.lng], from: [m.lat, m.lng], to: [m.lat, m.lng], start: 0, dur: 1,
      marker: L.marker([m.lat, m.lng], { icon: memberIcon }).bindPopup(m.label || "").addTo(group),
    }));
    group.addTo(map);
    extraLayerRef.current = group;
    if (!drift) return;
    let raf;
    const tick = (now) => {
      pins.forEach((p) => {
        const t = Math.min((now - p.start) / p.dur, 1);
        const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        const pos = [p.from[0] + (p.to[0] - p.from[0]) * e, p.from[1] + (p.to[1] - p.from[1]) * e];
        p.marker.setLatLng(pos);
        if (t >= 1) {
          p.from = pos;
          p.to = [p.home[0] + (Math.random() - 0.5) * 0.0016, p.home[1] + (Math.random() - 0.5) * 0.002];
          p.start = now;
          p.dur = 3000 + Math.random() * 2000;
        }
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [markers, drift]);

  return <div ref={containerRef} data-testid="leaflet-map" style={{ height, width: "100%", borderRadius: "12px", overflow: "hidden" }} />;
}
